import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Gift, QrCode, Tag, Ticket } from "lucide-react";
import { toast } from "sonner";
import { euro } from "@/cart";
import DiscountQr from "@/pages/admin/DiscountQr";

const API = process.env.REACT_APP_BACKEND_URL;
const inputCls = "h-11 w-full border border-neutral-800 bg-black/40 px-3 text-sm text-neutral-100 placeholder:text-neutral-600 focus:border-red-700 focus:outline-none";
const EMPTY = { code: "", kind: "percent", value: "", max_redemptions: "", expires_at: "", note: "" };

export default function DiscountsAdmin({ headers, onUnauthorized }) {
  const [codes, setCodes] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [qr, setQr] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/discounts`, { headers: headers() });
      setCodes(data);
    } catch (err) {
      if (err.response?.status === 401) onUnauthorized();
      else toast("Impossibile caricare i codici");
    }
  }, [headers, onUnauthorized]);
  useEffect(() => {
    load();
  }, [load]);

  const create = async (e) => {
    e.preventDefault();
    const value = parseFloat(String(form.value).replace(",", "."));
    if (!form.code.trim() || !Number.isFinite(value) || value <= 0) return toast("Inserisci codice e valore dello sconto");
    const body = {
      code: form.code.trim().toUpperCase().replace(/\s+/g, ""),
      note: form.note.trim(),
      max_redemptions: form.max_redemptions ? parseInt(form.max_redemptions, 10) : null,
      expires_at: form.expires_at || null,
      ...(form.kind === "percent" ? { percent_off: Math.round(value) } : { amount_off_cents: Math.round(value * 100) }),
    };
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/api/admin/discounts`, body, { headers: headers() });
      setCodes((list) => [data, ...(list || [])]);
      setForm(EMPTY);
      toast("Codice creato", { description: `${data.code} — da inserire al checkout` });
    } catch (err) {
      if (err.response?.status === 401) onUnauthorized();
      else toast(err.response?.data?.detail || "Creazione non riuscita");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (c) => {
    try {
      const { data } = await axios.patch(`${API}/api/admin/discounts/${c.id}`, {}, { headers: headers() });
      setCodes((list) => list.map((x) => (x.id === c.id ? data : x)));
      toast(data.active ? "Codice riattivato" : "Codice disattivato", { description: c.code });
    } catch (err) {
      toast(err.response?.data?.detail || "Operazione non riuscita");
    }
  };

  const setWelcome = async (c) => {
    try {
      const { data } = await axios.patch(`${API}/api/admin/discounts/${c.id}/welcome`, {}, { headers: headers() });
      setCodes((list) => list.map((x) => (x.id === c.id ? data : { ...x, welcome: data.welcome ? false : x.welcome })));
      toast(data.welcome ? "Codice inserito nell'email di benvenuto" : "Rimosso dall'email di benvenuto", { description: c.code });
    } catch (err) {
      toast(err.response?.data?.detail || "Operazione non riuscita");
    }
  };

  const welcomeCode = codes?.find((c) => c.welcome);

  return (
    <div className="mt-10">
      <h3 className="font-display flex items-center gap-2 text-3xl uppercase text-neutral-100"><Ticket className="h-5 w-5 text-red-600" /> Codici sconto</h3>
      <p className="mt-1 text-sm text-neutral-400">I fan li inseriscono nel carrello o nella pagina di pagamento Stripe. Con "QR" stampi un cartoncino da distribuire ai concerti: inquadrandolo il codice si applica da solo.</p>
      <p data-testid="welcome-discount-status" className="mt-2 flex items-center gap-2 text-sm text-neutral-300">
        <Gift className="h-4 w-4 text-red-600" />
        {welcomeCode
          ? <>Email di benvenuto: ogni nuovo iscritto riceve il codice <span className="font-mono-tac tracking-[0.15em] text-neutral-100">{welcomeCode.code}</span>.</>
          : <>Nessun codice nell'email di benvenuto. Usa il regalo accanto a un codice per attivarlo.</>}
      </p>
      <form data-testid="discount-form" onSubmit={create} className="mt-4 grid grid-cols-1 gap-3 border border-red-900/40 bg-[#0D0D0D] p-5 sm:grid-cols-3">
        <input data-testid="discount-code-input" value={form.code} onChange={set("code")} placeholder="Codice (es. LIVE10)" className={`${inputCls} uppercase`} />
        <div className="flex gap-2">
          <select data-testid="discount-kind-select" value={form.kind} onChange={set("kind")} className={`${inputCls} w-32`}>
            <option value="percent">% sconto</option>
            <option value="amount">€ fissi</option>
          </select>
          <input data-testid="discount-value-input" value={form.value} onChange={set("value")} inputMode="decimal" placeholder={form.kind === "percent" ? "es. 10" : "es. 5"} className={inputCls} />
        </div>
        <input data-testid="discount-max-input" value={form.max_redemptions} onChange={set("max_redemptions")} inputMode="numeric" placeholder="Max utilizzi (vuoto = illimitati)" className={inputCls} />
        <input data-testid="discount-expires-input" type="date" value={form.expires_at} onChange={set("expires_at")} className={inputCls} />
        <input data-testid="discount-note-input" value={form.note} onChange={set("note")} placeholder="Nota interna (es. concerto Gaeta)" className={inputCls} />
        <button data-testid="discount-create-button" type="submit" disabled={saving} className="h-11 bg-[#C8102E] px-5 font-mono-tac text-xs uppercase tracking-[0.2em] text-white hover:bg-[#FF2A4B] disabled:opacity-60">{saving ? "Creo…" : "Crea codice"}</button>
      </form>
      <div data-testid="admin-discounts-table" className="mt-4 overflow-x-auto border border-neutral-800 bg-[#0D0D0D]">
        <table className="w-full text-left text-sm">
          <thead className="font-mono-tac text-[10px] uppercase tracking-[0.25em] text-neutral-500">
            <tr className="border-b border-neutral-800"><th className="px-5 py-3">Codice</th><th className="px-5 py-3">Sconto</th><th className="px-5 py-3">Usi</th><th className="px-5 py-3">Scadenza</th><th className="px-5 py-3">Nota</th><th className="px-5 py-3">Benvenuto</th><th className="px-5 py-3" /></tr>
          </thead>
          <tbody>
            {codes?.length === 0 && <tr><td colSpan={7} className="px-5 py-8 text-center text-neutral-500">Nessun codice ancora.</td></tr>}
            {codes?.map((c) => (
              <tr key={c.id} data-testid="admin-discount-row" className={`border-b border-neutral-800/60 last:border-b-0 ${c.active ? "" : "opacity-50"}`}>
                <td className="px-5 py-3 font-mono-tac tracking-[0.15em] text-neutral-100"><Tag className="mr-2 inline h-3 w-3 text-red-600" />{c.code}</td>
                <td className="px-5 py-3 text-red-500">{c.percent_off ? `-${c.percent_off}%` : `-${euro(c.amount_off_cents)}`}</td>
                <td className="px-5 py-3 text-neutral-300">{c.times_used}{c.max_redemptions ? ` / ${c.max_redemptions}` : ""}</td>
                <td className="px-5 py-3 text-neutral-400">{c.expires_at || "—"}</td>
                <td className="max-w-[12rem] truncate px-5 py-3 text-neutral-500">{c.note || "—"}</td>
                <td className="px-5 py-3">
                  <button
                    data-testid="admin-discount-welcome-toggle"
                    onClick={() => setWelcome(c)}
                    disabled={!c.active}
                    title={c.welcome ? "Rimuovi dall'email di benvenuto" : "Usa nell'email di benvenuto"}
                    aria-pressed={!!c.welcome}
                    className={`inline-flex items-center gap-1.5 border px-3 py-1.5 font-mono-tac text-[10px] uppercase tracking-[0.2em] disabled:cursor-not-allowed ${c.welcome ? "border-red-700 bg-red-950/40 text-red-400" : "border-neutral-800 text-neutral-500 hover:border-red-800 hover:text-red-500"}`}
                  >
                    <Gift className="h-3 w-3" />{c.welcome ? "Nell'email" : "Usa"}
                  </button>
                </td>
                <td className="px-5 py-3 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button data-testid="admin-discount-qr" onClick={() => setQr(c)} disabled={!c.active} title="QR da stampare" aria-label={`QR ${c.code}`} className="inline-flex items-center gap-1.5 border border-neutral-700 px-3 py-1.5 font-mono-tac text-[10px] uppercase tracking-[0.2em] text-neutral-300 hover:border-red-600 hover:text-red-500 disabled:cursor-not-allowed disabled:opacity-40">
                      <QrCode className="h-3 w-3" /> QR
                    </button>
                    <button data-testid="admin-discount-toggle" onClick={() => toggle(c)} className={`border px-3 py-1.5 font-mono-tac text-[10px] uppercase tracking-[0.2em] ${c.active ? "border-neutral-700 text-neutral-400 hover:border-red-700 hover:text-red-500" : "border-red-900/60 text-red-500"}`}>
                      {c.active ? "Disattiva" : "Riattiva"}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {qr && <DiscountQr discount={qr} onClose={() => setQr(null)} />}
    </div>
  );
}
